import { getProfitabilityAnalysis } from "@/api/financialReport";
import { consoleHandlerError, extractErrorMessage } from "@/utils/message";

// Módulo del drawer "Análisis de Categorías" del tab Rentabilidad: la
// rentabilidad de UN período (el mes, el trimestre o el año que contiene el
// mes elegido) abierta por ítem de una categoría (tiendas/marcas). Espejo de
// `salesAnalysis`: el body del drawer lee de acá y las cards son
// presentacionales. El ítem del gráfico por ítem no es estado propio: es la
// selección del header cuando queda un solo ítem tildado (ver el body).

const DEFAULT_PERIOD = "month";

const state = () => ({
	/** YYYY-MM del mes elegido en el calendario. Null con el drawer cerrado. */
	month: null,
	/**
	 * Granularidad del bucket focal: "month" | "quarter" | "year". El bucket
	 * es el que contiene `month`; sus extremos llegan en `data.init_date` /
	 * `data.end_date`.
	 */
	period: DEFAULT_PERIOD,
	/** Categoría principal (la del chip o la del select del header). */
	category: null,
	/**
	 * Selección de los filtros del header: { tiendas: ["T1","T2"] }. Arranca
	 * vacía (= todos los ítems); lo que viaja al backend es sólo la selección
	 * PARCIAL — ver `effectiveFilters`.
	 */
	selectedItems: {},
	/** Payload del backend, congelado (sin observer profundo de Vue 2). */
	data: null,
	isLoading: false,
	error: null,
});

const mutations = {
	SET_CONTEXT(state, { month, category, selectedItems, period }) {
		state.month = month ?? null;
		state.period = period ?? DEFAULT_PERIOD;
		state.category = category ?? null;
		state.selectedItems = Object.freeze(selectedItems ?? {});
	},
	SET_DATA(state, payload) { state.data = payload; },
	SET_LOADING(state, isLoading) { state.isLoading = isLoading; },
	SET_ERROR(state, error) { state.error = error; },
	RESET(state) {
		state.month = null;
		state.period = DEFAULT_PERIOD;
		state.category = null;
		state.selectedItems = {};
		state.data = null;
		state.error = null;
	},
};

/**
 * Filtro que viaja al backend. Una categoría sin nada tildado (el estado con
 * el que abre el drawer) o con TODOS sus ítems tildados no filtra: hacerlo
 * dejaría fuera los documentos sin clasificar en ella ("Sin tienda") y el
 * total del drawer ya no cuadraría con la tabla del tab. Sólo viaja la
 * selección parcial.
 */
export function effectiveFilters(selectedItems, itemsByCategory = {}) {
	const filters = {};
	Object.entries(selectedItems || {}).forEach(([category, values]) => {
		const all = itemsByCategory[category] || [];
		if (values && values.length && values.length < all.length) {
			filters[category] = values;
		}
	});
	return Object.keys(filters).length ? filters : null;
}

const actions = {
	/**
	 * `period` conserva la granularidad vigente si no viene: cambiar de mes o
	 * de categoría no vuelve a Mensual. Al backend viaja sólo cuando no es la
	 * default, así la URL (y el cache) del modo mensual no cambia.
	 */
	async fetchProfitabilityAnalysis(
		{ commit, rootState, state },
		{ month, category = null, selectedItems = null, period = state.period } = {},
	) {
		commit("SET_CONTEXT", { month, category, selectedItems, period });
		commit("SET_LOADING", true);
		commit("SET_ERROR", null);
		try {
			const filters = effectiveFilters(
				selectedItems, rootState.classification.itemsByCategory
			);
			const response = await getProfitabilityAnalysis(
				month, category, filters, period === DEFAULT_PERIOD ? null : period,
			);
			commit("SET_DATA", Object.freeze(response.data));
		} catch (error) {
			consoleHandlerError({ error, prefix: "[profitabilityAnalysis]", silent: true });
			commit(
				"SET_ERROR",
				extractErrorMessage(error, "Error al cargar el análisis de categorías.")
			);
		} finally {
			commit("SET_LOADING", false);
		}
	},
};

export default {
	namespaced: true,
	state,
	mutations,
	actions,
};
